import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Batch 2 PR 2 · WorkOS bearer-token interceptor ──────────────────────
// AuthKit React stores the user's session in memory; we expose a setter
// that the AuthKitProvider effect calls on mount so axios can attach the
// access token to every outgoing request as `Authorization: Bearer <jwt>`.
// The token getter is a function reference so the latest token is read
// per-request (handles refresh without rebuilding the interceptor).
let _getAccessToken = null;

export function setAccessTokenGetter(fn) {
  _getAccessToken = typeof fn === "function" ? fn : null;
}

axios.interceptors.request.use(async (config) => {
  if (!_getAccessToken) return config;
  // Only attach to our backend; never leak to third-party hosts.
  const url = (config.url || "").toString();
  const base = (process.env.REACT_APP_BACKEND_URL || "").toString();
  if (base && !url.startsWith(base)) return config;
  try {
    const token = await _getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    /* unauthenticated · let the request go through; backend will 401 */
  }
  return config;
});

// ─── Request Dedup + Short TTL Cache ──────────────────────────────────────
// First-paint coalescing · the COP shell mounts ~28 components in parallel,
// many of which read the same endpoint (integrity, soc2, ledger, segments…).
// `gcache` collapses concurrent identical GETs into one network request and
// memoizes the result for `ttlMs` so re-mounts on tab switches stay cheap.
const _store = new Map();   // key -> { value, ts }
const _inflight = new Map(); // key -> Promise

const gcache = (key, fetcher, ttlMs = 30000) => {
  const now = Date.now();
  const hit = _store.get(key);
  if (hit && (now - hit.ts) < ttlMs) return Promise.resolve(hit.value);
  if (_inflight.has(key)) return _inflight.get(key);
  const p = fetcher()
    .then((v) => { _store.set(key, { value: v, ts: Date.now() }); _inflight.delete(key); return v; })
    .catch((e) => { _inflight.delete(key); throw e; });
  _inflight.set(key, p);
  return p;
};

// Force-refresh helper · invalidate after mutations
export const invalidateCache = (prefix) => {
  for (const k of _store.keys()) if (!prefix || k.startsWith(prefix)) _store.delete(k);
};

export const api = {
  directIndirect:    () => gcache("spend/direct-indirect", () => axios.get(`${API}/spend/direct-indirect`).then(r => r.data)),
  segments:          () => gcache("spend/segments", () => axios.get(`${API}/spend/segments`).then(r => r.data)),
  ledger:            (params={}) => gcache(`ledger:${JSON.stringify(params)}`, () => axios.get(`${API}/ledger`, { params }).then(r => r.data)),
  riskSentinel:      (limit=25) => gcache(`risk/sentinel:${limit}`, () => axios.get(`${API}/risk/sentinel`, { params: { limit } }).then(r => r.data)),
  integrity:         () => gcache("integrity/assertions", () => axios.get(`${API}/integrity/assertions`).then(r => r.data)),
  whoami:            () => gcache("auth/whoami", () => axios.get(`${API}/auth/whoami`).then(r => r.data), 30000),
  soc2:              () => gcache("compliance/soc2", () => axios.get(`${API}/compliance/soc2`).then(r => r.data)),
  router:            () => gcache("intelligence/router", () => axios.get(`${API}/intelligence/router`).then(r => r.data)),
  roadmapPhase2:     () => gcache("roadmap/phase2", () => axios.get(`${API}/roadmap/phase2`).then(r => r.data)),
  opportunitiesFunnel: (limit=200) => gcache(`opportunities/funnel:${limit}`, () => axios.get(`${API}/opportunities/funnel`, { params: { limit } }).then(r => r.data)),
  systemLearning:    (limit=40) => gcache(`system/learning:${limit}`, () => axios.get(`${API}/system/learning`, { params: { limit } }).then(r => r.data)),
  proofSavings:      (po_id) => axios.post(`${API}/proof/savings/${po_id}`).then(r => r.data),
  proofRisk:         (sup_id) => axios.post(`${API}/proof/risk/${sup_id}`).then(r => r.data),
  shouldCost:        (po_id) => axios.post(`${API}/sourcing/should-cost/${po_id}`).then(r => r.data),
  shouldCostDoer:    (po_id) => axios.post(`${API}/doers/should-cost/${po_id}`).then(r => r.data),
  rateBenchmark:     (po_id) => axios.post(`${API}/sourcing/rate-benchmark/${po_id}`).then(r => r.data),
  // ─── Wave 1 Chunk 9 · Should-Cost Compute Engine ──────────────────────────
  decisionQueue:     (params={}) => gcache(`should-cost/decisions:${JSON.stringify(params)}`,
                       () => axios.get(`${API}/should-cost/decisions`, { params }).then(r => r.data), 10000),
  decisionDetail:    (id) => axios.get(`${API}/should-cost/decisions/${id}`).then(r => r.data),
  decisionNarrative: (id) => axios.get(`${API}/should-cost/decisions/${id}/narrative`).then(r => r.data),
  inFlight:          (days=30) => gcache(`should-cost/in-flight:${days}`,
                       () => axios.get(`${API}/should-cost/in-flight`, { params: { days } }).then(r => r.data), 8000),
  sendKit:           (id, body) => axios.post(`${API}/should-cost/decisions/${id}/send-kit`, body).then((r) => { invalidateCache("should-cost/"); return r.data; }),
  kitSendHistory:    (id) => axios.get(`${API}/should-cost/decisions/${id}/send-history`).then(r => r.data),
  computeDecision:   (body) => axios.post(`${API}/should-cost/compute`, body).then((r) => { invalidateCache("should-cost/"); return r.data; }),
  batchCompute:      (body) => axios.post(`${API}/should-cost/batch`, body).then((r) => { invalidateCache("should-cost/"); return r.data; }),
  recomputeDecisions:(ids) => axios.post(`${API}/should-cost/recompute`, { decision_ids: ids }).then((r) => { invalidateCache("should-cost/"); return r.data; }),
  evidenceAudit:     () => axios.get(`${API}/should-cost/audit/evidence-traceable`).then(r => r.data),
  marketSegmentDrivers: () => gcache("market/segment-drivers", () => axios.get(`${API}/market/segment-drivers`).then(r => r.data)),
  marketPoLinkage:   (po_id) => axios.get(`${API}/market/po-linkage/${po_id}`).then(r => r.data),
  // Strategy Multiplier · Common Operating Picture
  strategyMultiplier:() => gcache("strategy/multiplier", () => axios.get(`${API}/strategy/multiplier`).then(r => r.data)),
  strategyMoves:     () => gcache("strategy/moves", () => axios.get(`${API}/strategy/moves`).then(r => r.data)),
  getStrategyMove:   (id) => axios.get(`${API}/strategy/moves/${id}`).then(r => r.data),
  createStrategyMove:(body) => axios.post(`${API}/strategy/moves`, body).then((r) => { invalidateCache("strategy/"); return r.data; }),
  addDialogueTurn:   (id, body) => axios.post(`${API}/strategy/moves/${id}/dialogue`, body).then(r => r.data),
  // Operator's Ledger (Retrospectives)
  retrospectives:    () => gcache("retrospectives", () => axios.get(`${API}/retrospectives`).then(r => r.data)),
  getRetrospective:  (id) => axios.get(`${API}/retrospectives/${id}`).then(r => r.data),
  createRetrospective:(body) => axios.post(`${API}/retrospectives`, body).then((r) => { invalidateCache("retrospectives"); return r.data; }),
  // Method Laboratory
  methodsSensors:    () => gcache("methods/sensors", () => axios.get(`${API}/methods/sensors`).then(r => r.data)),
  methods:           () => gcache("methods", () => axios.get(`${API}/methods`).then(r => r.data)),
  getMethod:         (id) => axios.get(`${API}/methods/${id}`).then(r => r.data),
  createMethod:      (body) => axios.post(`${API}/methods`, body).then((r) => { invalidateCache("methods"); return r.data; }),
  // Compliance Investigation
  complianceInvestigate: (slug) => axios.get(`${API}/compliance/investigate/${slug}`).then(r => r.data),
  categoryPackage:   (segment_code) => axios.get(`${API}/market/category-package/${segment_code}`).then(r => r.data),
  // Market Operating Picture
  marketSignals:     () => gcache("market/signals", () => axios.get(`${API}/market/signals`).then(r => r.data)),
  marketBriefing:    () => gcache("market/intelligence-briefing", () => axios.get(`${API}/market/intelligence-briefing`).then(r => r.data)),
  // SAM.gov Federal Live Feed · use plain fetch · axios was silently hanging
  // on these two routes (no .then, no .catch, no promise resolution observed)
  // even though the backend served 200s and direct browser fetch worked.
  // SAM.gov Federal Live Feed · use plain fetch · short 60s TTL since refresh
  // is on-demand or daily (no point in caching across the full session)
  samStatus: () => gcache(
    "market/sam-status",
    () => fetch(`${API}/market/sam-status`).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    }),
    60000,
  ),
  samFeed: (limit = 25) => gcache(
    `market/sam-feed:${limit}`,
    () => fetch(`${API}/market/sam-feed?limit=${limit}`).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    }),
    60000,
  ),
  samRefresh: () => fetch(`${API}/market/sam-refresh`, { method: "POST" })
    .then(async (r) => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      invalidateCache("market/sam-");
      return data;
    }),
  spendImport:       (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/spend/import`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  // Partner Collaborative Editing — Strategic Lead refinements committed to the Context Ledger
  saveRefinement:    (po_id, body) => axios.post(`${API}/refinements/${po_id}`, body).then(r => r.data),
  getRefinement:     (po_id) => axios.get(`${API}/refinements/${po_id}`).then(r => r.data),
  // Team Management · System of Record (MongoDB)
  teamRoles:         () => gcache("team/roles", () => axios.get(`${API}/team/roles`).then(r => r.data)),
  team:              () => gcache("team", () => axios.get(`${API}/team`).then(r => r.data)),
  teamCreate:        (body) => axios.post(`${API}/team`, body).then((r) => { invalidateCache("team"); return r.data; }),
  teamUpdate:        (id, patch) => axios.patch(`${API}/team/${id}`, patch).then((r) => { invalidateCache("team"); return r.data; }),
  teamDelete:        (id) => axios.delete(`${API}/team/${id}`).then((r) => { invalidateCache("team"); return r.data; }),
  teamSetActing:     (operative_id) => axios.post(`${API}/team/acting`, { operative_id }).then((r) => { invalidateCache("team"); return r.data; }),
  // ─── Wave 2.5 · Server-authoritative RBAC matrix ──────────────────────
  // Source-of-truth fetcher consumed by `lib/rbac.js · loadRbacMatrix()`.
  // No gcache · the matrix is read exactly once at app boot and cached in
  // the rbac module's own subscriber-aware store, so adding a layer of
  // request-coalescing here would be redundant complexity.
  rbacMatrix:        () => axios.get(`${API}/rbac/matrix`).then(r => r.data),
};

export const fmtUSD = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(Number(n || 0));

export const fmtUSDLong = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(Number(n || 0));

export const fmtCompact = (n) => new Intl.NumberFormat("en-US", {
  notation: "compact", maximumFractionDigits: 2,
}).format(Number(n || 0));

export const fmtPct = (n, d=2) => `${Number(n||0).toFixed(d)}%`;

// ─── PDI · Procurement Decision Intelligence ─────────────────────────────────
// PDI = 100 − risk_score · High PDI = more intelligence/integrity in the play.
// Tier mapping — universal 3-band traffic-light logic:
//   70-100 GREEN · 30-69 YELLOW (amber) · 0-29 RED
export const pdi = (riskScore) => Math.max(0, Math.min(100, 100 - Number(riskScore || 0)));
export const pdiTier = (riskScore) => {
  const v = pdi(riskScore);
  if (v >= 70) return "GREEN";
  if (v >= 30) return "YELLOW";
  return "RED";
};
